import type { ChatToolEvent } from '@fos/shared/contracts/copilot';
import {
  assignedElsewhereText,
  type ChatPlanCompany,
  type ChatPlanPreview,
  type ChatPlanStepPreview,
  COPILOT_ACTION_MESSAGES,
  type CopilotExecutedStep,
  type CopilotUndoStep,
  DELEGATED_TASKS_LABEL,
  planStepsToExecute,
} from '@fos/shared/contracts/copilot-actions';
import { COMPANY_VAT_STATUS_LABELS } from '@fos/shared/labels';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { useSession } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { toolEventHref } from '../copilot-ui';

/**
 * Działania Copilota w odpowiedzi (Fala 10, MVP §10): „Cofnij” przy wykonanym działaniu, „Cofnij wszystko” przy kilku
 * i plan do potwierdzenia jednym kliknięciem („Wykonaj”) — z wyborem przy niejednoznaczności (np. dwóch podobnych
 * klientów), danymi firmy po NIP i pytaniem o brakujący element (odpowiedź w rozmowie). Zapis i cofnięcie przez usługi
 * z uprawnieniami osoby (`copilot.executePlan`, `copilot.undo`, kanał `ui`).
 */

/** Odświeżenie list po działaniu Copilota (klienci, zadania, spotkania, wiedza, utrzymanie, monitoring, Dziś). */
export function useCopilotActionsInvalidate() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      utils.clients.invalidate(),
      utils.tasks.invalidate(),
      utils.meetings.invalidate(),
      utils.knowledge.invalidate(),
      utils.maintenance.invalidate(),
      utils.monitoring.invalidate(),
      queryClient.invalidateQueries({ queryKey: ['today'] }),
    ]);
}

/** Kroki „Cofnij wszystko” z działań jednej odpowiedzi: od ostatniego (odwrotna kolejność). */
export function undoStepsOf(events: readonly Pick<ChatToolEvent, 'undo'>[]): CopilotUndoStep[] {
  return [...events].reverse().flatMap((e) => e.undo ?? []);
}

/**
 * „Cofnij” / „Cofnij wszystko”: archiwizacja utworzonych rekordów albo poprzednie wartości zadania. W rozmowie wynik
 * zapisuje tura (`undone` + `onUndone`, 10.10.2026) — po zamknięciu i otwarciu panelu przycisk nie wraca.
 */
export function UndoButton({
  steps,
  label = 'Cofnij',
  undone,
  onUndone,
}: {
  steps: readonly CopilotUndoStep[];
  label?: string;
  /** podsumowanie wcześniejszego cofnięcia (zapisane w rozmowie) */
  undone?: string | null;
  onUndone?: (summary: string) => void;
}) {
  const undo = trpc.copilot.undo.useMutation();
  const invalidate = useCopilotActionsInvalidate();
  const [local, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const done = undone ?? local;
  if (steps.length === 0) return null;
  if (done) {
    return (
      <span role="status" className="text-caption text-fg-muted">
        {done}
      </span>
    );
  }
  const run = async () => {
    setError(null);
    try {
      const res = await undo.mutateAsync({ steps: [...steps] });
      setDone(res.summary);
      onUndone?.(res.summary);
      void invalidate();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się cofnąć. Zrób to ręcznie w module.'));
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button size="s" variant="ghost" icon={Undo2} loading={undo.isPending} onClick={() => void run()}>
        {label}
      </Button>
      {error && <FormMessage>{error}</FormMessage>}
    </span>
  );
}

function CompanyPreview({ company }: { company: ChatPlanCompany }) {
  if (!company.found) {
    return <p className="text-caption text-fg-muted">Nie znaleziono firmy z tym NIP w rejestrze.</p>;
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md bg-surface px-2.5 py-2 text-caption">
      <dt className="text-fg-muted">Nazwa</dt>
      <dd className="break-words text-fg">{company.legalName}</dd>
      {company.address && (
        <>
          <dt className="text-fg-muted">Adres</dt>
          <dd className="break-words text-fg">{company.address}</dd>
        </>
      )}
      {company.vatStatus && (
        <>
          <dt className="text-fg-muted">VAT</dt>
          <dd className="text-fg">{COMPANY_VAT_STATUS_LABELS[company.vatStatus]}</dd>
        </>
      )}
      <dt className="text-fg-muted">Źródło</dt>
      <dd className="text-fg">{company.simulated ? 'Rejestr (symulacja)' : 'Rejestr publiczny'}</dd>
    </dl>
  );
}

function PlanStepRow({
  step,
  chosen,
  onChoose,
  disabled,
}: {
  step: ChatPlanStepPreview;
  chosen: string | undefined;
  onChoose: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <li className="flex flex-col gap-1.5">
      <p className="text-body-s font-medium break-words text-fg">{step.label}</p>
      {step.details.map((d) => (
        <p key={d} className="text-caption break-words text-fg-secondary">
          {d}
        </p>
      ))}
      {step.company && <CompanyPreview company={step.company} />}
      {step.warning && <p className="text-caption text-warning">{step.warning}</p>}
      {step.duplicates && step.duplicates.length > 0 && (
        <p className="text-caption text-fg-muted">
          {step.duplicates.map((d, i) => (
            <span key={d.id}>
              {i > 0 && ', '}
              <Link to={`/klienci/${d.id}`} className="text-accent hover:underline">
                {d.name}
              </Link>
            </span>
          ))}
        </p>
      )}
      {step.question && <p className="text-caption text-fg">{step.question}</p>}
      {step.choices.length > 0 && (
        <fieldset className="flex flex-wrap gap-2" disabled={disabled}>
          <legend className="sr-only">{step.label}</legend>
          {step.choices.map((c) => (
            <Button
              key={c.id}
              size="s"
              variant="secondary"
              icon={chosen === c.id ? Check : undefined}
              aria-pressed={chosen === c.id}
              onClick={() => onChoose(c.id)}
            >
              {c.label}
            </Button>
          ))}
        </fieldset>
      )}
    </li>
  );
}

/** Adres listy „Zlecone przeze mnie” w Zadaniach (zadania, które dodałem i przypisałem innym). */
export const DELEGATED_TASKS_HREF = '/zadania?widok=lista&wykonawca=zlecone';

/**
 * Dopisek pod dodanym zadaniem, które trafiło do innej osoby (10.10.2026): „Przypisane do Marcin — zobaczysz je
 * w Zadaniach (Zespół albo Zlecone przeze mnie)”; „Zlecone przeze mnie” prowadzi do tej listy. Zadanie moje albo
 * bez osoby = bez dopisku.
 */
export function AssignedElsewhere({ assignee }: { assignee: { userId: string; name: string } | null | undefined }) {
  const session = useSession().data;
  const me = session?.authenticated ? session.user.id : null;
  if (!assignee || !me || assignee.userId === me) return null;
  const [before, after = ''] = assignedElsewhereText(assignee.name).split(DELEGATED_TASKS_LABEL);
  return (
    <p className="text-caption text-fg-muted">
      {before}
      <Link to={DELEGATED_TASKS_HREF} className="text-accent hover:underline">
        {DELEGATED_TASKS_LABEL}
      </Link>
      {after}
    </p>
  );
}

/** Wynik wykonanego planu: każdy krok z linkiem „Otwórz” (i dopiskiem o osobie), „Cofnij” / „Cofnij wszystko”. */
export function PlanDone({
  steps,
  undone,
  onUndone,
}: {
  steps: readonly CopilotExecutedStep[];
  undone?: string | null;
  onUndone?: (summary: string) => void;
}) {
  const visible = steps.filter((s) => s.action !== 'use_client');
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label="Wykonane działania" className="flex flex-col gap-1.5">
        {visible.map((s) => {
          const href = toolEventHref({ link: s.link, approvalId: null });
          return (
            <li key={s.index} className="flex flex-col gap-0.5">
              <span className="flex flex-wrap items-baseline gap-x-2 text-caption text-fg">
                <Check size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-success" />
                <span className="min-w-0 flex-1 break-words">{s.label}</span>
                {href && (
                  <Link to={href} className="text-accent hover:underline">
                    Otwórz
                  </Link>
                )}
              </span>
              {s.action === 'create_task' && (
                <span className="pl-[22px]">
                  <AssignedElsewhere assignee={s.assignee} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div>
        <UndoButton
          steps={[...visible].reverse().flatMap((s) => s.undo)}
          label={visible.filter((s) => s.undo.length).length > 1 ? 'Cofnij wszystko' : 'Cofnij'}
          undone={undone ?? null}
          {...(onUndone ? { onUndone } : {})}
        />
      </div>
    </div>
  );
}

/**
 * Plan działań do potwierdzenia: nic nie jest zapisane do kliknięcia „Wykonaj”. Wybór przy krokach z kilkoma
 * kandydatami; krok z pytaniem czeka na odpowiedź w rozmowie (Copilot przygotuje plan ponownie).
 *
 * 10.10.2026 (błąd karty): wynik „Wykonaj” trafia do tury rozmowy (`executed` / `onExecuted`) — karta po zamknięciu
 * i ponownym otwarciu panelu (Radix odmontowuje treść), zmianie ekranu albo odświeżeniu pokazuje wynik kroków, a nie
 * znowu „Wykonaj”. Bez `onExecuted` (np. test samego komponentu) wynik trzyma karta.
 */
export function ChatPlanCard({
  plan,
  onDone,
  executed,
  onExecuted,
  undone,
  onUndone,
}: {
  plan: ChatPlanPreview;
  onDone?: () => void;
  /** wynik zapisany w rozmowie (plan już wykonany) */
  executed?: CopilotExecutedStep[] | null;
  onExecuted?: (steps: CopilotExecutedStep[]) => void;
  undone?: string | null;
  onUndone?: (summary: string) => void;
}) {
  const execute = trpc.copilot.executePlan.useMutation();
  const invalidate = useCopilotActionsInvalidate();
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const [local, setResult] = useState<CopilotExecutedStep[] | null>(null);
  const result = executed ?? local;
  const [error, setError] = useState<string | null>(null);
  const steps = planStepsToExecute(plan, chosen);
  const asking = plan.steps.some((s) => s.question);
  const visible = plan.steps.filter((s) => s.action !== 'use_client' || s.choices.length > 0 || s.question);

  const run = async () => {
    if (!steps) return;
    setError(null);
    try {
      const res = await execute.mutateAsync({
        steps,
        ...(plan.conversationKey ? { conversationKey: plan.conversationKey } : {}),
      });
      setResult(res.steps);
      onExecuted?.(res.steps);
      onDone?.();
      void invalidate();
    } catch (err) {
      // błąd kroku z serwera („Krok 2 · Nowe zadanie: …”) + jasno: plan w jednej transakcji, nic nie zapisano
      const message = errorMessage(err, 'Nie udało się wykonać planu.').trim();
      setError(
        message.includes(COPILOT_ACTION_MESSAGES.nothingSaved)
          ? message
          : `${/[.!?]$/.test(message) ? message : `${message}.`} ${COPILOT_ACTION_MESSAGES.nothingSaved}`,
      );
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-canvas p-3">
      {/* po „Wykonaj” karta pokazuje już tylko wynik każdego kroku (bez planu i przycisku) */}
      {!result && (
        <ol aria-label="Plan działań" className="flex flex-col gap-3">
          {visible.map((s) => (
            <PlanStepRow
              key={s.index}
              step={s}
              chosen={chosen[s.index]}
              onChoose={(id) => setChosen((prev) => ({ ...prev, [s.index]: id }))}
              disabled={execute.isPending}
            />
          ))}
        </ol>
      )}
      {!result && plan.reasons.includes('untrusted') && (
        <p className="text-caption text-warning">{COPILOT_ACTION_MESSAGES.planUntrusted}</p>
      )}
      {result ? (
        <PlanDone steps={result} undone={undone ?? null} {...(onUndone ? { onUndone } : {})} />
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button size="s" variant="secondary" disabled={!steps} loading={execute.isPending} onClick={() => void run()}>
            Wykonaj
          </Button>
          <span className="text-caption text-fg-muted">
            {asking
              ? COPILOT_ACTION_MESSAGES.planMissing
              : steps
                ? COPILOT_ACTION_MESSAGES.planNeedsConfirmation
                : COPILOT_ACTION_MESSAGES.planChoice}
          </span>
        </div>
      )}
      {error && <FormMessage>{error}</FormMessage>}
    </div>
  );
}
